import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	collection,
	doc,
	getDocs,
	query,
	Timestamp,
	where,
	writeBatch,
} from 'firebase/firestore';
import { chunk } from 'lodash';
import { BROKER_GROUP_COLLECTION, firestore } from '@/utils/firebase';
import {
	ADDITIONAL_FOLDERS_COLLECTION,
	folderNamesMatch,
} from '@/utils/additionalFolderAutomation';
import { getActingUser } from '@/features/user/userApi';
import { BULK_CHUNK_SIZE, FIRESTORE_BATCH_LIMIT } from '@/utils/constants';

const parseAdditionalFolder = (snapshot) => ({
	id: snapshot.id,
	...JSON.parse(JSON.stringify(snapshot.data())),
});

const queryAdditionalFolderDocs = async (brokerGroupIds = [], pipelineId) => {
	if (brokerGroupIds.length === 0) return [];
	const folders = new Map();
	for (const brokerGroupChunk of chunk(brokerGroupIds, BULK_CHUNK_SIZE)) {
		const constraints = [where('brokerGroupId', 'in', brokerGroupChunk)];
		if (pipelineId) {
			constraints.push(where('pipelineId', '==', pipelineId));
		}
		const snapshot = await getDocs(
			query(collection(firestore, ADDITIONAL_FOLDERS_COLLECTION), ...constraints),
		);
		snapshot.docs.forEach((folderDoc) => {
			folders.set(folderDoc.id, folderDoc);
		});
	}

	return [...folders.values()];
};

export const additionalFoldersApi = createApi({
	reducerPath: 'additionalFoldersApi',
	tagTypes: ['ADDITIONAL_FOLDER'],
	baseQuery: fakeBaseQuery(),
	endpoints: (builder) => ({
		listAdditionalFolders: builder.query({
			providesTags: ['ADDITIONAL_FOLDER'],
			queryFn: async ({ brokerGroupIds = [], pipelineId } = {}) => {
				try {
					const docs = await queryAdditionalFolderDocs(brokerGroupIds, pipelineId);
					return { data: docs.map(parseAdditionalFolder) };
				} catch (error) {
					console.error('Failed to list additional folders', error);
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: 'Failed to list additional folders',
						},
					};
				}
			},
		}),
		saveAdditionalFolders: builder.mutation({
			invalidatesTags: ['ADDITIONAL_FOLDER'],
			queryFn: async ({ foldersToSave }, { dispatch, getState }) => {
				try {
					const user = await getActingUser(dispatch, getState);
					const brokerGroupIds = user?.brokerGroupArray;
					const userId = user?.userId;
					if (!userId || !brokerGroupIds?.length) {
						return {
							error: {
								status: 'CUSTOM_ERROR',
								error: 'Could not save additional folders',
							},
						};
					}

					const existing = await queryAdditionalFolderDocs(brokerGroupIds);
					const now = Timestamp.now();
					const remaining = existing.map((folderDoc) => ({
						id: folderDoc.id,
						ref: folderDoc.ref,
						...folderDoc.data(),
					}));
					const keptIds = new Set();
					const pendingWrites = [];

					const isFolderMatch = (folder, pipelineId, folderName, brokerGroupId) =>
						folder.pipelineId === pipelineId &&
						folderNamesMatch(folder.folderName, folderName) &&
						folder.brokerGroupId === brokerGroupId;

					// Each folder is one document for one group in this user's brokerGroupArray.
					// Removing it deletes that document. Other groups keep their own.
					Object.entries(foldersToSave).forEach(([pipelineId, folders]) => {
						folders.forEach((folderName) => {
							brokerGroupIds.forEach((brokerGroupId) => {
								const match = remaining.find((folder) =>
									isFolderMatch(folder, pipelineId, folderName, brokerGroupId),
								);
								if (match) {
									keptIds.add(match.id);
									return;
								}

								const folderRef = doc(
									collection(firestore, ADDITIONAL_FOLDERS_COLLECTION),
								);
								pendingWrites.push((batch) =>
									batch.set(folderRef, {
										brokerGroup: doc(
											firestore,
											BROKER_GROUP_COLLECTION,
											brokerGroupId,
										),
										brokerGroupId,
										folderName,
										parentFolderId: null,
										pipelineId,
										lastUpdated: now,
										lastUpdatedBy: userId,
										dateCreated: now,
										createdBy: userId,
									}),
								);
							});
						});
					});

					remaining.forEach((folder) => {
						if (keptIds.has(folder.id)) return;
						pendingWrites.push((batch) => batch.delete(folder.ref));
					});

					for (const writeChunk of chunk(pendingWrites, FIRESTORE_BATCH_LIMIT)) {
						const batch = writeBatch(firestore);
						writeChunk.forEach((write) => write(batch));
						await batch.commit();
					}
					return { data: { brokerGroupIds } };
				} catch (error) {
					console.error('Failed to save additional folders', error);
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: 'Failed to save additional folders',
						},
					};
				}
			},
		}),
	}),
});

export const {
	useListAdditionalFoldersQuery,
	useLazyListAdditionalFoldersQuery,
	useSaveAdditionalFoldersMutation,
} = additionalFoldersApi;
